import React, { useState, useEffect } from 'react';
import api from '../services/api';

const ComptabilitePremium = ({ clientId }) => {
  const [activeSubTab, setActiveSubTab] = useState('fec'); // 'fec' | 'relances'
  
  // States FEC
  const [fecUploading, setFecUploading] = useState(false);
  const [fecReport, setFecReport] = useState(null);
  
  // States Relances
  const [lateInvoices, setLateInvoices] = useState([]);
  const [loadingRelances, setLoadingRelances] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [relanceLevel, setRelanceLevel] = useState('amiable');
  const [generatingEmail, setGeneratingEmail] = useState(false);
  const [draftEmail, setDraftEmail] = useState(null);

  useEffect(() => {
    if (activeSubTab === 'relances') {
      fetchLateInvoices();
    }
  }, [activeSubTab, clientId]);

  const fetchLateInvoices = async () => {
    setLoadingRelances(true);
    try {
      const res = await api.get('/comptabilite/relances/echues');
      if (res && res.factures_en_retard) {
        setLateInvoices(res.factures_en_retard);
      }
    } catch (err) {
      console.error('Erreur chargement relances:', err);
    } finally {
      setLoadingRelances(false);
    }
  };

  const handleFecUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);

    setFecUploading(true);
    setFecReport(null);
    try {
      const response = await fetch('/comptabilite/fec/televerser', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        },
        body: formData
      });

      if (response.ok) {
        const result = await response.json();
        setFecReport(result);
      } else {
        const error = await response.json();
        alert(`Erreur d'analyse FEC : ${error.detail || 'Fichier non conforme'}`);
      }
    } catch (err) {
      console.error('Erreur upload FEC:', err);
      alert("Erreur lors de l'envoi du fichier FEC.");
    } finally {
      setFecUploading(false);
    }
  };

  const handleGenerateRelance = async () => {
    if (!selectedInvoice) return;
    setGeneratingEmail(true);
    setDraftEmail(null);
    try {
      const res = await api.post('/comptabilite/relances/generer-courriel', {
        document_id: selectedInvoice.id,
        niveau_relance: relanceLevel
      });
      if (res && res.status === 'success') {
        setDraftEmail(res);
      }
    } catch (err) {
      console.error('Erreur generation email:', err);
      alert("Erreur lors de la génération de l'e-mail.");
    } finally {
      setGeneratingEmail(false);
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden', padding: '8px' }}>
      
      {/* Sélecteur de sous-onglet */}
      <div style={{ display: 'flex', gap: '8px', borderBottom: '1px solid #E5E7EB', paddingBottom: '12px', marginBottom: '20px' }}>
        <button 
          onClick={() => setActiveSubTab('fec')}
          style={{
            background: activeSubTab === 'fec' ? '#0F6E56' : 'transparent',
            color: activeSubTab === 'fec' ? 'white' : '#4B5563',
            border: 'none',
            padding: '8px 16px',
            borderRadius: '8px',
            cursor: 'pointer',
            fontSize: '13px',
            fontWeight: '500',
            transition: 'all 0.2s'
          }}
        >
          📁 Analyse de FEC (Conformité DGFIP)
        </button>
        <button 
          onClick={() => setActiveSubTab('relances')}
          style={{
            background: activeSubTab === 'relances' ? '#0F6E56' : 'transparent',
            color: activeSubTab === 'relances' ? 'white' : '#4B5563',
            border: 'none',
            padding: '8px 16px',
            borderRadius: '8px',
            cursor: 'pointer',
            fontSize: '13px',
            fontWeight: '500',
            transition: 'all 0.2s'
          }}
        >
          ✉ Relances Clients (Créances échues)
        </button>
      </div>

      {activeSubTab === 'fec' && (
        <div style={{ display: 'flex', gap: '24px', flex: 1, overflow: 'hidden' }}>
          {/* Section d'upload et rapport */}
          <div style={{ flex: 1, background: 'white', padding: '24px', borderRadius: '16px', border: '1px solid #E8E7E3', display: 'flex', flexDirection: 'column', overflowY: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
              <div>
                <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#111827', margin: 0 }}>Vérification de Conformité FEC</h3>
                <p style={{ fontSize: '13px', color: '#6B7280', marginTop: '4px' }}>Analysez la structure et l'équilibre de votre Fichier des Écritures Comptables conforme DGFIP (A2F).</p>
              </div>

              <label style={{
                background: '#0F6E56',
                color: 'white',
                padding: '10px 16px',
                borderRadius: '10px',
                fontSize: '13px',
                fontWeight: '500',
                cursor: 'pointer',
                transition: 'background 0.2s',
              }}>
                {fecUploading ? 'Validation...' : '📁 Choisir un FEC (.txt)'}
                <input type="file" accept=".txt" onChange={handleFecUpload} disabled={fecUploading} style={{ display: 'none' }} />
              </label>
            </div>

            {fecUploading && (
              <div style={{ textAlign: 'center', padding: '60px' }}>
                <div style={{ width: '32px', height: '32px', border: '3px solid #0F6E56', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 1s linear infinite', margin: '0 auto 16px' }} />
                <p style={{ fontSize: '14px', color: '#4B5563' }}>Traitement des écritures en cours (validation structurelle, formatage des dates, balance)...</p>
              </div>
            )}

            {fecReport ? (
              <div>
                {/* Score circulaire */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '24px', background: '#F9FAFB', padding: '20px', borderRadius: '12px', marginBottom: '24px' }}>
                  <div style={{
                    width: '72px',
                    height: '72px',
                    borderRadius: '50%',
                    background: fecReport.score_conformite >= 90 ? '#E1F5EE' : (fecReport.score_conformite >= 70 ? '#FEF3C7' : '#FEE2E2'),
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '20px',
                    fontWeight: '700',
                    color: fecReport.score_conformite >= 90 ? '#0F6E56' : (fecReport.score_conformite >= 70 ? '#D97706' : '#DC2626')
                  }}>
                    {fecReport.score_conformite}%
                  </div>
                  <div>
                    <div style={{ fontSize: '16px', fontWeight: '600', color: '#111827' }}>Diagnostic : {fecReport.score_conformite >= 90 ? 'Conforme' : (fecReport.score_conformite >= 70 ? 'Avertissement structurel' : 'Non conforme')}</div>
                    <div style={{ fontSize: '13px', color: '#6B7280', marginTop: '2px' }}>
                      Fichier: <strong>{fecReport.nom_fichier}</strong> • {fecReport.nb_ecritures} lignes analysées
                    </div>
                  </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px', marginBottom: '24px' }}>
                  <div style={{ border: '1px solid #E5E7EB', padding: '16px', borderRadius: '10px', textAlign: 'center' }}>
                    <div style={{ fontSize: '12px', color: '#6B7280' }}>Total Débit</div>
                    <div style={{ fontSize: '16px', fontWeight: '600', color: '#111827', marginTop: '4px' }}>{fecReport.total_debit.toLocaleString('fr-FR')} €</div>
                  </div>
                  <div style={{ border: '1px solid #E5E7EB', padding: '16px', borderRadius: '10px', textAlign: 'center' }}>
                    <div style={{ fontSize: '12px', color: '#6B7280' }}>Total Crédit</div>
                    <div style={{ fontSize: '16px', fontWeight: '600', color: '#111827', marginTop: '4px' }}>{fecReport.total_credit.toLocaleString('fr-FR')} €</div>
                  </div>
                  <div style={{ border: '1px solid #E5E7EB', padding: '16px', borderRadius: '10px', textAlign: 'center', background: fecReport.ecart > 0.05 ? '#FEE2E2' : 'white' }}>
                    <div style={{ fontSize: '12px', color: '#6B7280' }}>Écart Équilibre</div>
                    <div style={{ fontSize: '16px', fontWeight: '600', color: fecReport.ecart > 0.05 ? '#DC2626' : '#111827', marginTop: '4px' }}>{fecReport.ecart.toLocaleString('fr-FR')} €</div>
                  </div>
                </div>

                {fecReport.en_tetes_manquants.length > 0 && (
                  <div style={{ marginBottom: '20px' }}>
                    <h4 style={{ fontSize: '13px', fontWeight: '600', color: '#B45309', marginBottom: '8px' }}>⚠️ En-têtes réglementaires DGFIP manquants :</h4>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                      {fecReport.en_tetes_manquants.map((h, idx) => (
                        <span key={idx} style={{ background: '#FEF3C7', color: '#D97706', fontSize: '12px', padding: '4px 8px', borderRadius: '6px' }}>{h}</span>
                      ))}
                    </div>
                  </div>
                )}

                <div>
                  <h4 style={{ fontSize: '14px', fontWeight: '600', color: '#111827', marginBottom: '12px' }}>Anomalies structurelles détectées :</h4>
                  {fecReport.total_anomalies === 0 ? (
                    <div style={{ background: '#F0FDF4', color: '#15803D', padding: '16px', borderRadius: '8px', fontSize: '13px' }}>
                      ✅ Aucune anomalie détectée sur les lignes. La structure du fichier est saine.
                    </div>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      {fecReport.anomalies.map((err, idx) => (
                        <div key={idx} style={{ background: '#FFFBEB', borderLeft: '4px solid #F59E0B', padding: '12px', borderRadius: '6px', fontSize: '13px' }}>
                          <strong>Ligne {err.ligne}</strong> ({err.type}) : {err.details}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ) : (
              !fecUploading && (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', border: '2px dashed #E5E7EB', borderRadius: '16px', padding: '80px 20px', textAlign: 'center' }}>
                  <span style={{ fontSize: '48px', marginBottom: '16px' }}>📁</span>
                  <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#374151' }}>Prêt à l'analyse FEC</h3>
                  <p style={{ fontSize: '13px', color: '#6B7280', maxWidth: '340px', marginTop: '6px' }}>Déposez le fichier `.txt` d'écritures comptables exporté de votre logiciel actuel (SAGE, Quadra, Ibiza, etc.).</p>
                </div>
              )
            )}
          </div>
        </div>
      )}

      {activeSubTab === 'relances' && (
        <div style={{ display: 'flex', gap: '24px', flex: 1, overflow: 'hidden' }}>
          {/* Liste des factures impayées */}
          <div style={{ flex: 1, background: 'white', padding: '24px', borderRadius: '16px', border: '1px solid #E8E7E3', display: 'flex', flexDirection: 'column', overflowY: 'auto' }}>
            <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#111827', marginBottom: '4px' }}>Factures de Vente Impayées Échues</h3>
            <p style={{ fontSize: '13px', color: '#6B7280', marginBottom: '20px' }}>Liste des ventes dont le délai d'échéance de paiement est dépassé.</p>

            {loadingRelances ? (
              <div style={{ textAlign: 'center', padding: '40px', color: '#6B7280' }}>Chargement...</div>
            ) : lateInvoices.length === 0 ? (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '60px 20px', textAlign: 'center', border: '1px solid #E5E7EB', borderRadius: '12px' }}>
                <span style={{ fontSize: '32px', marginBottom: '12px' }}>🎉</span>
                <h4 style={{ fontSize: '14px', fontWeight: '600', color: '#1F2937' }}>Aucun impayé en retard !</h4>
                <p style={{ fontSize: '12px', color: '#6B7280', marginTop: '4px' }}>Toutes vos créances clients sont à jour ou ont été réconciliées avec la banque.</p>
              </div>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                  <thead>
                    <tr style={{ borderBottom: '1px solid #E5E7EB', textAlign: 'left' }}>
                      <th style={{ padding: '12px 8px', color: '#4B5563', fontWeight: '600' }}>Client</th>
                      <th style={{ padding: '12px 8px', color: '#4B5563', fontWeight: '600' }}>Facture</th>
                      <th style={{ padding: '12px 8px', color: '#4B5563', fontWeight: '600' }}>Échéance</th>
                      <th style={{ padding: '12px 8px', color: '#4B5563', fontWeight: '600' }}>Montant TTC</th>
                      <th style={{ padding: '12px 8px', color: '#4B5563', fontWeight: '600' }}>Retard</th>
                      <th style={{ padding: '12px 8px', color: '#4B5563', fontWeight: '600', textAlign: 'right' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {lateInvoices.map((inv) => (
                      <tr 
                        key={inv.id} 
                        onClick={() => { setSelectedInvoice(inv); setDraftEmail(null); }}
                        style={{
                          borderBottom: '1px solid #F3F4F6',
                          background: selectedInvoice?.id === inv.id ? '#F0FDF9' : 'white',
                          cursor: 'pointer',
                          transition: 'all 0.15s'
                        }}
                      >
                        <td style={{ padding: '12px 8px', fontWeight: '600', color: '#111827' }}>{inv.client_nom}</td>
                        <td style={{ padding: '12px 8px', color: '#4B5563' }}>{inv.nom_fichier}</td>
                        <td style={{ padding: '12px 8px', color: '#4B5563' }}>{inv.date_echeance}</td>
                        <td style={{ padding: '12px 8px', fontWeight: '600', color: '#0F6E56' }}>{inv.montant_ttc.toLocaleString('fr-FR')} €</td>
                        <td style={{ padding: '12px 8px' }}>
                          <span style={{ background: '#FEE2E2', color: '#DC2626', fontSize: '11px', fontWeight: '600', padding: '2px 8px', borderRadius: '20px' }}>
                            +{inv.jours_retard} j
                          </span>
                        </td>
                        <td style={{ padding: '12px 8px', textAlign: 'right' }}>
                          <button style={{ background: '#0F6E56', color: 'white', border: 'none', padding: '4px 8px', borderRadius: '6px', fontSize: '11px', fontWeight: '500', cursor: 'pointer' }}>
                            Relancer ↗
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Panneau de relances IA */}
          <div style={{ width: '400px', background: 'white', padding: '24px', borderRadius: '16px', border: '1px solid #E8E7E3', display: 'flex', flexDirection: 'column', overflowY: 'auto' }}>
            {selectedInvoice ? (
              <div>
                <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#111827', marginBottom: '16px', borderBottom: '1px solid #F3F4F6', paddingBottom: '12px' }}>
                  Générateur de Relance IA
                </h3>

                <div style={{ marginBottom: '16px', fontSize: '13px' }}>
                  <div style={{ color: '#6B7280' }}>Client concerné :</div>
                  <div style={{ fontWeight: '600', color: '#111827', marginTop: '2px' }}>{selectedInvoice.client_nom}</div>
                </div>

                <div style={{ marginBottom: '20px', fontSize: '13px' }}>
                  <div style={{ color: '#6B7280' }}>Facture / Montant dû :</div>
                  <div style={{ fontWeight: '600', color: '#0F6E56', marginTop: '2px' }}>{selectedInvoice.nom_fichier} • {selectedInvoice.montant_ttc.toLocaleString('fr-FR')} €</div>
                </div>

                {/* Sélecteur de niveau */}
                <div style={{ marginBottom: '20px' }}>
                  <label style={{ fontSize: '12px', fontWeight: '600', color: '#4B5563', display: 'block', marginBottom: '6px' }}>Niveau de relance :</label>
                  <select 
                    value={relanceLevel} 
                    onChange={(e) => setRelanceLevel(e.target.value)}
                    style={{ width: '100%', padding: '8px 12px', borderRadius: '8px', border: '1px solid #D1D5DB', fontSize: '13px', outline: 'none' }}
                  >
                    <option value="amiable">✉ 1ère relance (Amiable / Simple)</option>
                    <option value="ferme">✉ 2ème relance (Ferme / Insistant)</option>
                    <option value="mise_en_demeure">⚖ 3ème relance (Mise en demeure formelle)</option>
                  </select>
                </div>

                <button 
                  onClick={handleGenerateRelance}
                  disabled={generatingEmail}
                  style={{
                    width: '100%',
                    background: '#0F6E56',
                    color: 'white',
                    border: 'none',
                    padding: '10px 16px',
                    borderRadius: '8px',
                    fontSize: '13px',
                    fontWeight: '500',
                    cursor: 'pointer',
                    marginBottom: '20px',
                    transition: 'background 0.2s'
                  }}
                >
                  {generatingEmail ? 'Rédaction IA en cours...' : '✍ Générer le courriel'}
                </button>

                {draftEmail && (
                  <div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                      <span style={{ fontSize: '12px', fontWeight: '600', color: '#4B5563' }}>Brouillon du message :</span>
                      <button 
                        onClick={() => {
                          navigator.clipboard.writeText(`Objet : ${draftEmail.subject}\n\n${draftEmail.body}`);
                          alert('✅ Contenu copié dans le presse-papier !');
                        }}
                        style={{ background: 'transparent', border: 'none', color: '#0F6E56', fontSize: '12px', fontWeight: '500', cursor: 'pointer' }}
                      >
                        📋 Copier
                      </button>
                    </div>
                    <div style={{ background: '#FAF9F5', padding: '12px', borderRadius: '8px', border: '1px solid #E5E7EB', fontSize: '12px', fontFamily: 'monospace', overflowX: 'auto', whiteSpace: 'pre-wrap', maxHeight: '280px', overflowY: 'auto' }}>
                      <strong>Objet:</strong> {draftEmail.subject}
                      <br /><br />
                      {draftEmail.body}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: '#9CA3AF', textAlign: 'center', minHeight: '300px' }}>
                <span style={{ fontSize: '32px', marginBottom: '12px' }}>✉</span>
                <p style={{ fontSize: '13px', margin: 0 }}>Sélectionnez une facture impayée pour rédiger un e-mail de relance automatique par l'IA.</p>
              </div>
            )}
          </div>
        </div>
      )}

      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default ComptabilitePremium;
